import type { WashChemical } from "@station/contracts";
import { Decimal } from "@station/core";
import { Alert, Button, Card, EmptyState, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ClipboardCheck, FlaskConical, PackageCheck, Warehouse } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useWashChemicals } from "../../../features/wash/queries.js";
import {
  TableFrame,
  Th,
  washSearchShape,
  WashStationPage,
  type WashStationContext,
} from "../../../features/wash/shared.js";
import { COVER_TONE, coverLevel } from "../../../features/wash/wash-values.js";
import { errorMessage } from "../../../lib/errors.js";

const searchSchema = z.object(washSearchShape);

export const Route = createFileRoute("/_app/yikama/kimyasallar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ChemicalsRoute,
});

/** Kapsama günü bu değerin altındaysa "az" uyarısı (bilgi amaçlı eşik; sipariş önerisi market modülündedir). */
const LOW_COVER_DAYS = 7;

/**
 * Yıkama kimyasalları (Prompt 18 web, ADR 0018 §6): istasyonun YIKAMA deposundaki bakiyeler, son 30 gün tüketimi,
 * günlük ortalama ve kapsama günü (az kalanlar uyarılır). İstasyonda MARKET kapalıysa stok tutulmaz: tüketim reçeteden
 * TEORİK hesaplanır ("teorik" rozeti). Alım / sayım / stok market ekranlarındadır — bağlantılar yıkama deposunu taşır.
 */
function ChemicalsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <WashStationPage title={t("menu.washChemicals")} description={t("wash.chemicals.description")}>
      {(context) => <ChemicalsBody context={context} />}
    </WashStationPage>
  );
}

const quantityText = (value: string | null, scale = 3) =>
  value === null ? "—" : Decimal.from(value).formatTr(scale).replace(/,?0+$/, "") || "0";

function ChemicalsBody({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const { station, permissions } = context;
  const chemicals = useWashChemicals(station.id);

  if (chemicals.error) return <Alert tone="danger">{errorMessage(chemicals.error)}</Alert>;
  if (!chemicals.data) return <SkeletonRows rows={5} />;
  const data = chemicals.data;
  const lowCount = data.items.filter((item) => {
    const level = coverLevel(item, LOW_COVER_DAYS);
    return level === "low" || level === "none";
  }).length;
  const marketSearch = { company: station.companyId, station: station.id };

  return (
    <>
      <Card className="flex flex-wrap items-center gap-3 p-4">
        <div className="grid gap-1">
          <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {data.stockTracked ? t("wash.chemicals.tracked") : t("wash.chemicals.untracked")}
            {!data.stockTracked ? (
              <StatusBadge tone="warning">{t("wash.chemicals.theoretical")}</StatusBadge>
            ) : null}
          </span>
          <span className="text-xs text-muted-foreground">
            {t("wash.chemicals.period", { days: data.periodDays, threshold: LOW_COVER_DAYS })}
          </span>
        </div>
        {data.stockTracked && permissions.canOpenMarket ? (
          <div className="ms-auto flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/market/mal-kabul" search={marketSearch as never}>
                <PackageCheck aria-hidden="true" />
                {t("wash.chemicals.receipts")}
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/market/sayimlar" search={marketSearch as never}>
                <ClipboardCheck aria-hidden="true" />
                {t("wash.chemicals.counts")}
              </Link>
            </Button>
            {data.warehouseId ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/market/stok" search={{ ...marketSearch, warehouse: data.warehouseId } as never}>
                  <Warehouse aria-hidden="true" />
                  {t("wash.chemicals.stock")}
                </Link>
              </Button>
            ) : null}
          </div>
        ) : null}
      </Card>

      {!data.stockTracked ? <Alert tone="info">{t("wash.chemicals.untrackedHint")}</Alert> : null}
      {data.stockTracked && data.warehouseId === null ? (
        <Alert tone="info">{t("wash.chemicals.noWarehouse")}</Alert>
      ) : null}
      {lowCount > 0 ? (
        <Alert tone="warning" title={t("wash.chemicals.lowTitle", { count: lowCount })}>
          {t("wash.chemicals.lowHint", { threshold: LOW_COVER_DAYS })}
        </Alert>
      ) : null}
      {data.warnings.length > 0 ? (
        <Alert tone="warning">
          <ul className="grid gap-1">
            {data.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {data.items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<FlaskConical />}
          title={t("wash.chemicals.emptyTitle")}
          description={t("wash.chemicals.emptyDescription")}
        />
      ) : (
        <TableFrame label={t("wash.chemicals.caption")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("wash.chemicals.product")}</Th>
              <Th align="right">{t("wash.chemicals.quantity")}</Th>
              <Th align="right">{t("wash.chemicals.value")}</Th>
              <Th align="right">{t("wash.chemicals.consumed", { days: data.periodDays })}</Th>
              <Th align="right">{t("wash.chemicals.dailyAverage")}</Th>
              <Th>{t("wash.chemicals.cover")}</Th>
              <Th>{t("wash.chemicals.programs")}</Th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <ChemicalRow key={item.productId} item={item} theoretical={!data.stockTracked} />
            ))}
          </tbody>
        </TableFrame>
      )}
    </>
  );
}

function ChemicalRow({ item, theoretical }: { item: WashChemical; theoretical: boolean }): ReactElement {
  const { t } = useTranslation();
  const unit = t(`enums.productUnit.${item.unit}`);
  const level = coverLevel(item, LOW_COVER_DAYS);
  return (
    <tr className="border-t">
      <td className="px-3 py-2">
        <span className="grid gap-0.5">
          <span className="font-medium">{item.name}</span>
          <span className="font-mono text-xs text-muted-foreground">{item.code}</span>
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular whitespace-nowrap">
        {item.quantity === null ? (
          <span className="text-muted-foreground">{t("wash.chemicals.notTracked")}</span>
        ) : (
          `${quantityText(item.quantity)} ${unit}`
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <span className="grid justify-items-end gap-0.5">
          <MoneyText value={item.value} />
          {item.averageUnitCost ? (
            <span className="text-xs text-muted-foreground">
              {t("wash.chemicals.averageCost", {
                cost: Decimal.from(item.averageUnitCost).formatTr(2),
                unit,
              })}
            </span>
          ) : null}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5">
          {quantityText(item.consumedLastPeriod)} {unit}
          {theoretical ? <StatusBadge tone="warning">{t("wash.chemicals.theoretical")}</StatusBadge> : null}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular whitespace-nowrap">
        {quantityText(item.dailyAverage)} {unit}
      </td>
      <td className="px-3 py-2">
        <StatusBadge tone={COVER_TONE[level]} className="whitespace-nowrap">
          {level === "unknown"
            ? t("wash.chemicals.coverUnknown")
            : level === "none"
              ? t("wash.chemicals.coverNone")
              : t(level === "low" ? "wash.chemicals.coverLow" : "wash.chemicals.coverOk", {
                  days: Decimal.from(item.daysOfCover ?? "0").formatTr(1),
                })}
        </StatusBadge>
      </td>
      <td className="px-3 py-2 text-xs">
        {item.usedInPrograms.length === 0 ? (
          <span className="text-muted-foreground">{t("wash.chemicals.unused")}</span>
        ) : (
          <span className="font-mono">{item.usedInPrograms.join(", ")}</span>
        )}
      </td>
    </tr>
  );
}
